<template>
  <div class="compact-grid-wrap" ref="gridRoot">
    <div class="compact-grid">
      <div
          v-for="item in visibleImages"
          :key="item.id"
          class="compact-card"
          :class="{ 'is-disabled': !canEdit(item.time) }">
        <div class="compact-card__preview">
          <div
              class="compact-card__preview-link"
              @click.stop="onPreviewClick($event, item)">
            <img
                :src="getPreviewSrc(item)"
                class="compact-card__image"
                @error="onImageError">
          </div>
          <div v-if="isVideoFile(item.url)" class="compact-card__video-mark">视频</div>
        </div>

        <div class="compact-card__meta">
          <div class="compact-card__meta-text">
            <div class="compact-card__meta-line">
              <span class="meta-label">新热度</span>{{ item.hotness != null ? item.hotness : '-' }}
            </div>
            <div class="compact-card__meta-line">
              <span class="meta-label">热度</span>{{ item.heat != null ? item.heat : '-' }}
            </div>
          </div>
          <el-popover
              placement="top"
              width="200"
              trigger="hover"
              :open-delay="150"
              :close-delay="200"
              :value="openPopoverId === item.id"
              @input="onPopoverInput($event, item.id)">
            <div class="compact-card__meta-tip">
              <div>类型 {{ getTypeLabel(item) }}</div>
              <div>热度 {{ item.heat != null ? item.heat : '-' }}</div>
              <div>日期 {{ formatShortUploadDate(item.time) }}</div>
              <div>id {{ item.id }}</div>
              <div v-if="canEdit(item.time)" class="compact-card__meta-actions">
                <el-button type="primary" size="mini" icon="el-icon-edit" @click="onEdit(item)">编辑</el-button>
                <el-button type="danger" size="mini" icon="el-icon-delete" @click="onDelete(item)">删除</el-button>
              </div>
            </div>
            <i
                slot="reference"
                class="el-icon-more compact-card__meta-more"
                @click.stop></i>
          </el-popover>
        </div>

        <div :data-tag-row-id="item.id" class="compact-card__tag image-tag-input-wrap">
          <textarea
              v-model="item.tag"
              class="compact-card__tag-input"
              rows="2"
              wrap="soft"
              placeholder="图片标签"
              :disabled="!canEdit(item.time)"
              @click.stop
              @keydown.enter.prevent="canEdit(item.time) && onTagEnter(item)"
              @keydown="onTagKeydown($event, item)"></textarea>
        </div>
      </div>
    </div>
    <div v-if="images.length === 0" class="compact-grid-empty">
      暂无数据
    </div>
  </div>
</template>

<script>
import {
  isVideoFile,
  getVideoSnapshot,
  getMaterialTypeLabel,
  formatShortUploadDate
} from './imageMaterialUtils'

const PREVIEW_SNAPSHOT_SIZE = 165
const RENDER_BATCH_SIZE = 60
const DEFAULT_IMAGE_ERROR = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iODAiIGhlaWdodD0iODAiIHZpZXdCb3g9IjAgMCA4MCA4MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjgwIiBoZWlnaHQ9IjgwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0zMiA1NlY0NEw0OCA1MEwzMiA1NloiIGZpbGw9IiM5OTk5OTkiLz4KPC9zdmc+'

export default {
  name: 'ImageCompactGrid',
  props: {
    images: {
      type: Array,
      default: function () {
        return []
      }
    },
    canEdit: {
      type: Function,
      required: true
    }
  },
  data: function () {
    return {
      renderCount: 0,
      openPopoverId: null
    }
  },
  computed: {
    visibleImages: function () {
      if (this.renderCount <= 0) {
        return []
      }
      return this.images.slice(0, this.renderCount)
    }
  },
  watch: {
    images: {
      immediate: true,
      handler: function (list) {
        this.openPopoverId = null
        this.startBatchRender(list ? list.length : 0)
      }
    }
  },
  beforeDestroy: function () {
    this.cancelBatchRender()
  },
  methods: {
    isVideoFile: isVideoFile,
    formatShortUploadDate: formatShortUploadDate,
    getTypeLabel: function (item) {
      return getMaterialTypeLabel(item)
    },
    getPreviewSrc: function (item) {
      if (isVideoFile(item.url)) {
        return getVideoSnapshot(item.url, PREVIEW_SNAPSHOT_SIZE)
      }
      return item.url
    },
    cancelBatchRender: function () {
      if (this._batchRafId) {
        cancelAnimationFrame(this._batchRafId)
        this._batchRafId = null
      }
    },
    startBatchRender: function (total) {
      this.cancelBatchRender()
      if (!total) {
        this.renderCount = 0
        return
      }
      this.renderCount = Math.min(RENDER_BATCH_SIZE, total)
      if (this.renderCount >= total) {
        return
      }
      const that = this
      const step = function () {
        if (that.renderCount >= total) {
          that._batchRafId = null
          return
        }
        that.renderCount = Math.min(that.renderCount + RENDER_BATCH_SIZE, total)
        if (that.renderCount < total) {
          that._batchRafId = requestAnimationFrame(step)
        } else {
          that._batchRafId = null
        }
      }
      this._batchRafId = requestAnimationFrame(step)
    },
    ensureItemRendered: function (index) {
      const total = this.images.length
      if (index < 0 || total <= 0) {
        return
      }
      const required = Math.min(index + 1, total)
      if (this.renderCount < required) {
        this.cancelBatchRender()
        this.renderCount = required
      }
    },
    onTagEnter: function (row) {
      this.$emit('tag-enter', row)
    },
    onTagKeydown: function (event, row) {
      this.$emit('tag-keydown', event, row)
    },
    onPreviewClick: function (event, row) {
      this.$emit('preview-click', event, row)
    },
    onPopoverInput: function (visible, id) {
      this.openPopoverId = visible ? id : (this.openPopoverId === id ? null : this.openPopoverId)
    },
    onEdit: function (row) {
      this.openPopoverId = null
      this.$emit('edit', row)
    },
    onDelete: function (row) {
      this.openPopoverId = null
      this.$emit('delete', row.id)
    },
    onImageError: function (event) {
      event.target.src = DEFAULT_IMAGE_ERROR
    }
  }
}
</script>

<style scoped>
.compact-grid-wrap {
  width: 100%;
}

.compact-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(169px, 1fr));
  gap: 11px;
}

.compact-grid-empty {
  text-align: center;
  padding: 40px 0;
  color: #909399;
  font-size: 14px;
}

.compact-card {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #ebeef5;
  border-radius: 4px;
  padding: 3px;
  background: #fff;
}

.compact-card.is-disabled {
  opacity: 0.6;
}

.compact-card__preview {
  position: relative;
  width: 100%;
  height: 132px;
  /* 中灰双色棋盘（不含纯白）：透明见格子，白色像素会明显更亮 */
  background-color: #b0b0b0;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16'%3E%3Cpath fill='%238a8a8a' d='M0 0h8v8H0zm8 8h8v8H8z'/%3E%3C/svg%3E");
  background-size: 16px 16px;
  background-repeat: repeat;
  border-radius: 2px;
  box-sizing: border-box;
  overflow: hidden;
  margin-bottom: 4px;
}

.compact-card__preview-link {
  display: block;
  width: 100%;
  height: 100%;
  cursor: zoom-in;
}

.compact-card__image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.compact-card__video-mark {
  position: absolute;
  top: 4px;
  right: 4px;
  padding: 2px 6px;
  background: rgba(255, 200, 0, 0.9);
  color: #000;
  font-size: 11px;
  font-weight: bold;
  border-radius: 3px;
  line-height: 1.2;
  pointer-events: none;
}

.compact-card__meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 2px;
  font-size: 13px;
  color: #303133;
  line-height: 1.3;
  margin-bottom: 5px;
}

.compact-card__meta-text {
  flex: 1;
  min-width: 0;
}

.compact-card__meta-line {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.35;
}

.compact-card__meta-line + .compact-card__meta-line {
  margin-top: 2px;
}

.compact-card__meta-more {
  flex-shrink: 0;
  font-size: 15px;
  color: #909399;
  cursor: pointer;
  padding: 2px;
  transform: rotate(90deg);
}

.compact-card__meta-more:hover {
  color: #409eff;
}

.compact-card__meta-tip {
  line-height: 1.6;
  font-size: 13px;
  color: #303133;
}

.compact-card__meta-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 10px;
  padding-top: 8px;
  border-top: 1px solid #ebeef5;
}

.meta-label {
  color: #909399;
  margin-right: 4px;
}

.compact-card__tag {
  width: 100%;
}

.compact-card__tag-input {
  width: 100%;
  height: 58px;
  line-height: 1.35;
  padding: 4px 6px;
  border: 1px solid #dcdfe6;
  border-radius: 4px;
  font-size: 15px;
  color: #606266;
  box-sizing: border-box;
  outline: none;
  background: #fff;
  resize: none;
  font-family: inherit;
  overflow-wrap: break-word;
  word-break: break-word;
  white-space: pre-wrap;
}

.compact-card__tag-input:focus {
  border-color: #409eff;
}

.compact-card__tag-input:disabled {
  background: #f5f7fa;
  color: #c0c4cc;
  cursor: not-allowed;
}
</style>
